import { Injectable, inject } from '@angular/core';
import { Analytics, logEvent } from '@angular/fire/analytics';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analytics = inject(Analytics);

  private readonly APP_ID = 'admin';

  /**
   * Log an analytics event with app_id automatically included
   * @param eventName - The name of the event
   * @param eventParams - Additional parameters for the event
   */
  logEvent(eventName: string, eventParams?: { [key: string]: any }): void {
    try {
      const params = {
        ...eventParams,
        app_id: this.APP_ID
      };
      logEvent(this.analytics, eventName, params);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }

  /**
   * Log a screen view event
   * @param screenName - The name of the screen/page
   * @param additionalParams - Additional parameters
   */
  logScreenView(screenName: string, additionalParams?: { [key: string]: any }): void {
    this.logEvent('screen_view', {
      screen_name: screenName,
      ...additionalParams
    });
  }

  /**
   * Log a user action event
   * @param action - The action performed
   * @param category - The category of the action
   * @param label - Optional label
   * @param value - Optional numeric value
   */
  logUserAction(action: string, category: string, label?: string, value?: number): void {
    this.logEvent('user_action', {
      action,
      category,
      label,
      value
    });
  }

  /**
   * Log a search event
   * @param searchTerm - The search term
   * @param searchType - Type of search (venue, user, etc.)
   */
  logSearch(searchTerm: string, searchType: string): void {
    this.logEvent('search', {
      search_term: searchTerm,
      search_type: searchType
    });
  }

  /**
   * Log a venue-related event
   * @param action - The action performed (view, edit, etc.)
   * @param venueId - The venue ID
   */
  logVenueEvent(action: string, venueId: string): void {
    this.logEvent('venue_interaction', {
      action,
      venue_id: venueId
    });
  }

  /**
   * Log a user lookup event
   * @param userId - The user ID searched
   * @param success - Whether the lookup was successful
   */
  logUserLookup(userId: string, success: boolean): void {
    this.logEvent('user_lookup', {
      user_id: userId,
      success
    });
  }
}
